// デバイス列挙 / 出力切替対応判定の薄いラッパ (spec §4.4 Step 2/3)。
// Web 標準 API (enumerateDevices / setSinkId) のみに依存する (原則 4)。
export type AudioDevice = { deviceId: string; label: string };
export type AudioDeviceLists = { inputs: AudioDevice[]; outputs: AudioDevice[] };

export async function listAudioDevices(): Promise<AudioDeviceLists> {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.enumerateDevices) {
    return { inputs: [], outputs: [] };
  }
  const all = await navigator.mediaDevices.enumerateDevices();
  const inputs: AudioDevice[] = [];
  const outputs: AudioDevice[] = [];
  for (const d of all) {
    // 権限付与前は label が空になる (spec §4.4 Step 1)。無名でも選べるよう連番 fallback
    if (d.kind === "audioinput") {
      inputs.push({ deviceId: d.deviceId, label: d.label || `マイク ${inputs.length + 1}` });
    } else if (d.kind === "audiooutput") {
      outputs.push({ deviceId: d.deviceId, label: d.label || `スピーカー ${outputs.length + 1}` });
    }
  }
  return { inputs, outputs };
}

export function subscribeDeviceChange(onChange: () => void): () => void {
  const md = typeof navigator !== "undefined" ? navigator.mediaDevices : undefined;
  if (!md?.addEventListener) return () => undefined;
  md.addEventListener("devicechange", onChange);
  return () => md.removeEventListener("devicechange", onChange);
}

export function supportsOutputSelection(): boolean {
  return (
    typeof HTMLMediaElement !== "undefined" &&
    typeof (HTMLMediaElement.prototype as { setSinkId?: unknown }).setSinkId === "function"
  );
}

// enumerateDevices が返す「既定」「通信用」は実体を指さない合成エントリ。
// Chrome は内蔵スピーカーしか無い端末でも default (と環境により communications) を
// 含めて返すため、生の件数では「選び分けられる出力先があるか」を判定できない。
const SYNTHETIC_OUTPUT_IDS = new Set(["default", "communications"]);

/**
 * 実際に選び分けられる出力先の数 (D26-5)。
 *
 * 対処モードの手順② が「下の『スピーカー』から選び直す」と書いてよいかの判定に使う。
 * 同じ物理デバイスが複数の deviceId で現れる実装差までは畳めない (groupId が要る) が、
 * AudioDevice 型を広げる波及を避け、既知の 2 値を除くに留める (spec §4.1 の限界)。
 */
export function countSelectableOutputs(devices: AudioDevice[]): number {
  return devices.filter((d) => !SYNTHETIC_OUTPUT_IDS.has(d.deviceId)).length;
}
